import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { useTranslation } from 'react-i18next';

/**
 * Twitter-style sticky sub-header.
 *
 * - Full-bleed: the bar itself stretches the full width of its parent
 *   (which is the page-container, not the centered content column).
 * - Sticky: stays pinned to the top while the content scrolls.
 * - Left-pinned: the back button + title hug the left edge of the
 *   viewport on desktop, ignoring the centered max-width used by the
 *   body content below it. On mobile the layout is unchanged.
 * - Right slot: optional area for action buttons (menu, filters, etc.).
 *
 * Should be placed *inside* a `page-container` and *above* the page's
 * centered content wrapper so the header looks like a separate band.
 */
export default function PageSubHeader({
  onBack,
  title,
  subtitle,
  rightSlot,
  testId,
  backTestId,
}) {
  const {
    t
  } = useTranslation();

  return (
    <div
      className="sticky top-[57px] z-40 w-full backdrop-blur-md"
      style={{
        background: 'color-mix(in srgb, var(--theme-background) 85%, transparent)',
        borderBottom: '1px solid var(--glass-border, var(--theme-border))',
      }}
      data-testid={testId || 'page-sub-header'}
    >
      <div className="flex items-center gap-3 px-4 lg:px-6 py-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="rounded-full shrink-0 -ml-1"
          data-testid={backTestId || 'page-sub-header-back'}
          aria-label={t('generated.pagesubheader_back_1d4e')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          {title && (
            <h1
              className="text-base font-semibold truncate"
              style={{ color: 'var(--theme-text)' }}
            >
              {title}
            </h1>
          )}
          {subtitle && (
            <p
              className="text-xs truncate"
              style={{ color: 'var(--theme-textSecondary)' }}
            >
              {subtitle}
            </p>
          )}
        </div>
        {rightSlot && <div className="shrink-0 flex items-center gap-1">{rightSlot}</div>}
      </div>
    </div>
  );
}
